import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Box,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Business,
  LocationOn,
  CalendarToday,
  ExpandMore,
  ExpandLess,
  Work,
  Assignment,
  CheckCircle,
  RadioButtonUnchecked,
  Schedule,
  AttachMoney,
  Visibility,
  PlayArrow,
  Cancel,
  Info,
  TaskAlt,
  AssignmentTurnedIn,
  VideoCall,
  Description
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Applications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedApp, setExpandedApp] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [jobTasks, setJobTasks] = useState([]);
  const [tasksDialog, setTasksDialog] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskSubmissions, setTaskSubmissions] = useState({});
  const [pendingAssessments, setPendingAssessments] = useState([]);

  const applicationStages = [
    { 
      key: 'Applied', 
      label: 'Application Submitted',
      icon: <CheckCircle />,
      description: 'Your application has been received'
    },
    { 
      key: 'Screening', 
      label: 'Initial Screening',
      icon: <Description />,
      description: 'Your profile is being reviewed'
    },
    { 
      key: 'Technical Assessment', 
      label: 'Technical Assessment',
      icon: <Assignment />,
      description: 'Complete assigned tasks and assessments'
    },
    { 
      key: 'Interview', 
      label: 'Interview Round',
      icon: <VideoCall />,
      description: 'Interview with the hiring team'
    },
    { 
      key: 'Offer', 
      label: 'Offer Stage',
      icon: <Work />,
      description: 'Salary negotiation and offer letter'
    },
    { 
      key: 'Hired', 
      label: 'Hired',
      icon: <TaskAlt />,
      description: 'Welcome to the team!'
    }
  ];

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/applications/my-applications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const apps = response.data.data || [];
      setApplications(apps);
      
      // Identify pending assessments
      const pending = apps.filter(app => app.currentStage === 'Technical Assessment');
      setPendingAssessments(pending);
      
      setError('');
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobTasks = async (jobId, applicationId) => {
    try {
      setLoadingTasks(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5000/api/tasks/job/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tasks = response.data.data || [];
      
      // Check submission status for each task
      const tasksWithStatus = await Promise.all(tasks.map(async (task) => {
        try {
          const submissionResponse = await axios.get(
            `http://localhost:5000/api/tasks/submission/check/${task._id}/${applicationId}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          return {
            ...task,
            submission: submissionResponse.data.data
          };
        } catch (err) {
          // No submission found
          return { ...task, submission: null };
        }
      }));
      
      setJobTasks(tasksWithStatus);
    } catch (err) {
      console.error('Error fetching job tasks:', err);
      setJobTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleExpandClick = (appId) => {
    setExpandedApp(expandedApp === appId ? null : appId);
  };

  const handleViewTasks = async (application) => {
    setSelectedJob(application.job);
    setSelectedApplication(application);
    setTasksDialog(true);
    if (application.job?._id) {
      await fetchJobTasks(application.job._id, application._id);
    }
  };

  const handleStartTask = (taskId, applicationId) => {
    navigate(`/assessment/${applicationId}/${taskId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'info',
      'under_review': 'warning',
      'shortlisted': 'success',
      'interview_scheduled': 'primary',
      'rejected': 'error',
      'hired': 'success',
      'withdrawn': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'submitted': 'Submitted',
      'under_review': 'Under Review',
      'shortlisted': 'Shortlisted',
      'interview_scheduled': 'Interview Scheduled',
      'rejected': 'Rejected',
      'hired': 'Hired',
      'withdrawn': 'Withdrawn'
    };
    return labels[status] || status;
  };

  const getCurrentStageIndex = (stage) => {
    const index = applicationStages.findIndex(s => s.key === stage);
    return index >= 0 ? index : 0;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatSalary = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculateDaysSince = (date) => {
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchApplications}>Retry</Button>
      </Container>
    );
  }

  if (applications.length === 0) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          My Applications
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Applications Yet
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            You haven't applied to any positions yet.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/jobs')}
            startIcon={<Work />}
          >
            Browse Jobs
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          My Applications
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Track your job applications and their current status
        </Typography>
      </Box>

      {/* Pending Assessments Banner */}
      {pendingAssessments.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, p: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              variant="contained"
              startIcon={<Assignment />}
              onClick={() => {
                // Scroll to first assessment
                const firstAssessment = document.querySelector('[data-assessment="true"]');
                if (firstAssessment) {
                  firstAssessment.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              View Assessments
            </Button>
          }
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              🚨 You have {pendingAssessments.length} pending assessment{pendingAssessments.length > 1 ? 's' : ''}!
            </Typography>
            <Typography variant="body1">
              Complete your technical assessments to continue in the hiring process. 
              <strong> These assessments are required to move forward.</strong>
            </Typography>
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {applications.map((application) => {
          const isInAssessmentStage = application.currentStage === 'Technical Assessment';
          const wasAutoProgressed = application.job?.selectionProcess?.skipScreening && 
                                    !application.job?.selectionProcess?.skipAssessment;
          
          return (
            <Grid item xs={12} key={application._id}>
              <Card sx={{ mb: 2, border: isInAssessmentStage ? 2 : 0, borderColor: 'primary.main' }}
                    data-assessment={isInAssessmentStage ? "true" : "false"}>
                <CardContent>
                  {/* Assessment Alert for Auto-progressed Candidates */}
                  {isInAssessmentStage && wasAutoProgressed && (
                    <Alert 
                      severity="warning" 
                      sx={{ mb: 2 }}
                      action={
                        <Button 
                          color="inherit" 
                          size="small"
                          variant="contained"
                          startIcon={<PlayArrow />}
                          onClick={() => handleViewTasks(application)}
                        >
                          Start Assessment
                        </Button>
                      }
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        ⚠️ Assessment Required - Action Needed!
                      </Typography>
                      <Typography variant="body2">
                        You've been automatically advanced to the assessment stage. <strong>Complete the tasks now to continue in the hiring process.</strong>
                      </Typography>
                    </Alert>
                  )}

                  {/* Assessment Alert for Manual Progression */}
                  {isInAssessmentStage && !wasAutoProgressed && (
                    <Alert 
                      severity="info" 
                      sx={{ mb: 2 }}
                      action={
                        <Button 
                          color="inherit" 
                          size="small"
                          variant="contained"
                          startIcon={<Assignment />}
                          onClick={() => handleViewTasks(application)}
                        >
                          View Tasks
                        </Button>
                      }
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        📋 Technical Assessment Available
                      </Typography>
                      <Typography variant="body2">
                        Tasks are ready for you to complete. <strong>Finish your assessment to move forward.</strong>
                      </Typography>
                    </Alert>
                  )}

                  {/* Application Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {application.job?.title || 'Position'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          icon={<Business />}
                          label={application.job?.department || 'Department'}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<LocationOn />}
                          label={application.job?.location || 'Location'}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={getStatusLabel(application.status)}
                          color={getStatusColor(application.status)}
                          size="small"
                        />
                        {isInAssessmentStage && (
                          <Chip
                            icon={<Assignment />}
                            label="Assessment Phase"
                            color="primary"
                            size="small"
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Applied {calculateDaysSince(application.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {isInAssessmentStage && !wasAutoProgressed && (
                        <Badge badgeContent="Tasks Available" color="primary">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Assignment />}
                            onClick={() => handleViewTasks(application)}
                          >
                            View Tasks
                          </Button>
                        </Badge>
                      )}
                      <IconButton
                        onClick={() => handleExpandClick(application._id)}
                        aria-expanded={expandedApp === application._id}
                      >
                        {expandedApp === application._id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      Application Progress
                    </Typography>
                    <Typography variant="caption" color="primary">
                      {application.currentStage}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(getCurrentStageIndex(application.currentStage) + 1) / applicationStages.length * 100}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>

                {/* Expanded Content */}
                <Collapse in={expandedApp === application._id} timeout="auto" unmountOnExit>
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Application Timeline */}
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Application Timeline
                  </Typography>
                  <Stepper 
                    activeStep={getCurrentStageIndex(application.currentStage)} 
                    orientation="vertical"
                    sx={{ mt: 2 }}
                  >
                    {applicationStages.map((stage, index) => {
                      const isCompleted = index < getCurrentStageIndex(application.currentStage);
                      const isCurrent = stage.key === application.currentStage;
                      const stageHistory = application.stageHistory?.find(h => h.stage === stage.key);
                      
                      return (
                        <Step key={stage.key} completed={isCompleted}>
                          <StepLabel
                            StepIconComponent={() => (
                              <Box sx={{ color: isCompleted || isCurrent ? 'primary.main' : 'text.disabled' }}>
                                {React.cloneElement(stage.icon, { fontSize: 'small' })}
                              </Box>
                            )}
                          >
                            <Box>
                              <Typography variant="subtitle2">
                                {stage.label}
                              </Typography>
                              {stageHistory && (
                                <Typography variant="caption" color="textSecondary">
                                  {formatDate(stageHistory.enteredAt)}
                                </Typography>
                              )}
                            </Box>
                          </StepLabel>
                          <StepContent>
                            <Typography variant="body2" color="textSecondary">
                              {stage.description}
                            </Typography>
                            {isCurrent && stage.key === 'Technical Assessment' && (
                              <Box sx={{ mt: 2 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Assignment />}
                                  onClick={() => handleViewTasks(application)}
                                >
                                  View & Complete Tasks
                                </Button>
                              </Box>
                            )}
                          </StepContent>
                        </Step>
                      );
                    })}
                  </Stepper>

                  {/* Additional Details */}
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                      {application.candidateProfile?.expectedSalary && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">
                            Expected Salary (Monthly)
                          </Typography>
                          <Typography variant="body2">
                            {formatSalary(application.candidateProfile.expectedSalary)}
                          </Typography>
                        </Grid>
                      )}
                      {application.candidateProfile?.availableDate && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary">
                            Available From
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(application.candidateProfile.availableDate)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Cover Letter */}
                  {application.candidateProfile?.coverLetter && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Cover Letter
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                          {application.candidateProfile.coverLetter}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* Tasks Dialog */}
      <Dialog
        open={tasksDialog}
        onClose={() => setTasksDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Tasks for {selectedJob?.title}
            </Typography>
            <IconButton onClick={() => setTasksDialog(false)} size="small">
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingTasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : jobTasks.length === 0 ? (
            <Alert severity="info">
              No tasks have been assigned for this job yet. Please check back later.
            </Alert>
          ) : (
            <List>
              {jobTasks.map((task, index) => (
                <React.Fragment key={task._id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon>
                      {task.completed ? (
                        <CheckCircle color="success" />
                      ) : (
                        <RadioButtonUnchecked color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={task.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {task.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Chip
                              icon={<Schedule />}
                              label={(() => {
                                // Handle new duration format
                                if (task.duration && task.duration.value && task.duration.unit) {
                                  const { value, unit } = task.duration;
                                  return value === 1 ? `${value} ${unit.slice(0, -1)}` : `${value} ${unit}`;
                                }
                                // Handle old timeLimit format
                                if (task.timeLimit) {
                                  return task.timeLimit === 1 ? '1 day' : `${task.timeLimit} days`;
                                }
                                return '7 days';
                              })()}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={task.type || task.taskType || 'Task'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {task.difficulty && (
                              <Chip
                                label={task.difficulty}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ ml: 2 }}>
                      {task.submission?.status === 'submitted' || task.submission?.status === 'evaluated' ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled
                            sx={{ mb: 1 }}
                          >
                            Assessment Submitted
                          </Button>
                          <Typography variant="caption" display="block" color="textSecondary">
                            {task.submission?.submittedAt ? 
                              new Date(task.submission.submittedAt).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'Submitted'
                            }
                          </Typography>
                        </Box>
                      ) : task.submission?.status === 'in-progress' ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<PlayArrow />}
                          onClick={() => handleStartTask(task._id, selectedApplication?._id)}
                        >
                          Resume Assignment
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PlayArrow />}
                          onClick={() => handleStartTask(task._id, selectedApplication?._id)}
                        >
                          Start Task
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTasksDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Applications;
